"use client";

import React, { forwardRef, useEffect, useLayoutEffect, useRef } from "react";
import Quill from "quill";
import "quill/dist/quill.snow.css";

const Embed = Quill.import('blots/embed') as any;

// Custom Embed for variable chips - truly non-editable inline element
class VariableChip extends (Embed as any) {
  static blotName = 'variableChip';
  static tagName = 'span';
  static className = 'variable-chip-embed';

  static create(value: { label: string; placeholder: string }) {
    const node = super.create() as HTMLElement;
    node.setAttribute('contenteditable', 'false');
    node.setAttribute('data-variable', value.placeholder);
    node.setAttribute('data-label', value.label);
    node.textContent = value.label;
    
    // Make it truly non-editable
    node.style.cssText = `
      background: var(--primary);
      color: white;
      padding: 3px 10px;
      border-radius: 14px;
      font-size: 13px;
      font-weight: 500;
      margin: 0 3px;
      display: inline-block;
      cursor: default;
      user-select: none;
      vertical-align: middle;
      pointer-events: none;
    `;
    
    return node;
  }

  static value(node: HTMLElement) {
    return {
      label: node.getAttribute('data-label') || node.textContent || '',
      placeholder: node.getAttribute('data-variable') || '',
    };
  }
  
  // Override to prevent editing
  update(mutations: MutationRecord[]) {
    // Do nothing - prevents updates
  }
}

// Compatibility embed for mobile-created content (flutter_quill BlockEmbed key: "variable")
class VariableBlock extends (Embed as any) {
  static blotName = 'variable';
  static tagName = 'span';
  static className = 'variable-chip-embed';

  static create(value: any) {
    const node = super.create() as HTMLElement;
    node.setAttribute('contenteditable', 'false');
    const label = typeof value === 'string'
      ? value
      : (value?.label || value?.data || value?.placeholder || '');
    node.textContent = label;
    node.style.cssText = `
      background: var(--primary);
      color: white;
      padding: 3px 10px;
      border-radius: 14px;
      font-size: 13px;
      font-weight: 500;
      margin: 0 3px;
      display: inline-block;
      cursor: default;
      user-select: none;
      vertical-align: middle;
      pointer-events: none;
    `;
    return node;
  }

  static value(node: HTMLElement) {
    return node.textContent || '';
  }

  update(mutations: MutationRecord[]) {
    // Do nothing - prevents updates
  }
}

// Register the custom embed
Quill.register('formats/variableChip', VariableChip);
Quill.register('formats/variable', VariableBlock);

// Editor is an uncontrolled React component
const Editor = forwardRef<
  Quill,
  {
    readOnly?: boolean;
    defaultValue?: any;
    onTextChange?: (delta: any, oldDelta: any, source: string) => void;
    onSelectionChange?: (range: any, oldRange: any, source: string) => void;
    placeholder?: string;
  }
>(
  (
    {
      readOnly,
      defaultValue,
      onTextChange,
      onSelectionChange,
      placeholder = "Write description...",
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const defaultValueRef = useRef(defaultValue);
    const onTextChangeRef = useRef(onTextChange);
    const onSelectionChangeRef = useRef(onSelectionChange);

    useLayoutEffect(() => {
      onTextChangeRef.current = onTextChange;
      onSelectionChangeRef.current = onSelectionChange;
    });

    useEffect(() => {
      if (ref && "current" in ref) {
        ref.current?.enable(!readOnly);
      }
    }, [ref, readOnly]);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;

      const editorContainer = container.appendChild(
        container.ownerDocument.createElement("div"),
      );

      const options = {
        debug: false,
        modules: {
          toolbar: [
            [{ header: [1, 2, 3, 4, 5, false] }],
            ["bold", "italic", "underline", "link"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ align: [] }],
            ["clean"],
          ],
        },
        placeholder: placeholder,
        theme: "snow",
      };

      const quill = new Quill(editorContainer, options);

      if (ref && "current" in ref) {
        ref.current = quill;
      }

      if (defaultValueRef.current) {
        quill.setContents(defaultValueRef.current);
      }

      quill.on(Quill.events.TEXT_CHANGE, (...args) => {
        onTextChangeRef.current?.(...args);
      });

      quill.on(Quill.events.SELECTION_CHANGE, (...args) => {
        onSelectionChangeRef.current?.(...args);
      });

      return () => {
        if (ref && "current" in ref) {
          ref.current = null;
        }
        container.innerHTML = "";
      };
    }, [ref]);

    return <div ref={containerRef} className="quill-wrapper"></div>;
  },
);

Editor.displayName = "Editor";

type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialValue?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (value: any) => void;
  placeholder?: string;
  height?: number;
  onEditorReady?: (quill: Quill) => void;
};

export default function QuillEditor({
  initialValue,
  onChange,
  placeholder = "Write description...",
  height = 100,
  onEditorReady,
}: Props) {
  const quillRef = useRef<Quill>(null);
  const normalizeInitialValue = (value: any) => {
    if (!value) return undefined;
    if (typeof value === "object") return value;
    if (typeof value === "string") {
      try {
        return JSON.parse(value);
      } catch {
        return { ops: [{ insert: value }] };
      }
    }
    return undefined;
  };

  useEffect(() => {
    if (quillRef.current && onEditorReady) {
      onEditorReady(quillRef.current);
    }
  }, [onEditorReady]);

  useEffect(() => {
    if (!quillRef.current) return;
    const nextValue = normalizeInitialValue(initialValue);
    if (!nextValue) return;

    const current = quillRef.current.getContents();
    const currentOps = (current as any)?.ops || [];
    const nextOps = (nextValue as any)?.ops || [];

    // Keep Quill and parent state synchronized without remounting/focus loss.
    if (JSON.stringify(currentOps) !== JSON.stringify(nextOps)) {
      const currentSelection = quillRef.current.getSelection();
      quillRef.current.setContents(nextValue, "silent");
      if (currentSelection) {
        quillRef.current.setSelection(currentSelection, "silent");
      }
    }
  }, [initialValue]);

  const handleTextChange = () => {
    if (quillRef.current) {
      const delta = quillRef.current.getContents();
      // Convert to a plain serializable Delta format (plain object with ops)
      const serializableDelta = delta && (delta as any).ops ? { ops: (delta as any).ops } : JSON.parse(JSON.stringify(delta));
      onChange(serializableDelta);
    }
  };

  const handleContainerClick = () => {
    if (quillRef.current) {
      quillRef.current.focus();
    }
  };

  return (
    <div
      className="p-[5px] bg-background border border-border rounded-xl overflow-visible relative cursor-text"
      onClick={handleContainerClick}
    >
      <div className="quill-wrapper">
        <Editor
          ref={quillRef}
          defaultValue={normalizeInitialValue(initialValue)}
          onTextChange={handleTextChange}
          placeholder={placeholder}
        />
      </div>

      <style>{`
        /* Remove default Quill borders */
        .quill-wrapper .ql-toolbar.ql-snow,
        .quill-wrapper .ql-container.ql-snow {
          border: none !important;
        }

        /* Quill Base Layout */
        .quill-wrapper .ql-toolbar {
          @apply flex border-0 p-2.5 bg-transparent;
        }

        .quill-wrapper .ql-container {
          @apply border-0 font-inherit;
        }

        .quill-wrapper .ql-editor {
          @apply bg-transparent text-foreground p-3 overflow-y-auto font-inherit text-sm;
          height: ${height}px !important;
          max-height: ${height}px !important;
        }

        .quill-wrapper .ql-editor.ql-blank::before {
          color: rgb(var(--muted-foreground)) !important;
          font-style: italic !important;
          left: 15px !important;
          pointer-events: none !important;
          position: absolute !important;
          right: 15px !important;
        }

        /* Variable Chip Styles */
        .quill-wrapper .variable-chip-embed {
          background: var(--primary) !important;
          color: white !important;
          padding: 3px 10px !important;
          border-radius: 14px !important;
          font-size: 13px !important;
          font-weight: 500 !important;
          margin: 0 3px !important;
          display: inline-block !important;
          cursor: default !important;
          user-select: none !important;
          vertical-align: middle !important;
          pointer-events: none !important;
        }

        .quill-wrapper .variable-chip-embed:hover {
          opacity: 0.85;
        }

        /* Utility classes for controls */
        .controls {
          @apply flex border border-border border-t-0 p-2.5;
        }

        .controls-right {
          @apply ml-auto;
        }

        .state {
          @apply my-2.5 font-mono;
        }

        .state-title {
          @apply text-muted-foreground uppercase;
        }

        /* Quill Dropdown - Only what can't be done with Tailwind */
        .ql-picker-options,
        #ql-picker-options-2,
        [id^="ql-picker-options"],
        span[id="ql-picker-options-2"],
        .ql-snow .ql-picker-options {
          background: var(--secondary-background) !important;
          border: 1px solid var(--border) !important;
          border-radius: 8px !important;
          box-shadow:
            0 10px 15px -3px rgb(0 0 0 / 0.1),
            0 4px 6px -4px rgb(0 0 0 / 0.1) !important;
          padding: 6px !important;
          max-height: 200px !important;
          overflow-y: auto !important;
          overflow-x: hidden !important;
        }

        .ql-picker-item,
        #ql-picker-options-2 .ql-picker-item,
        [id^="ql-picker-options"] .ql-picker-item {
          color: var(--foreground) !important;
          background: transparent !important;
          padding: 10px 14px !important;
          border-radius: 6px !important;
          font-size: 14px !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          margin: 2px 0 !important;
          min-height: auto !important;
          display: block !important;
          width: calc(100% - 4px) !important;
          margin-left: 2px !important;
          margin-right: 2px !important;
        }

        .ql-picker-item:hover,
        #ql-picker-options-2 .ql-picker-item:hover,
        [id^="ql-picker-options"] .ql-picker-item:hover,
        .ql-picker-item.ql-selected,
        #ql-picker-options-2 .ql-picker-item.ql-selected,
        [id^="ql-picker-options"] .ql-picker-item.ql-selected {
          background: var(--primary) !important;
          color: var(--primary-foreground) !important;
          transform: none !important;
        }

        .ql-picker-item.ql-selected,
        #ql-picker-options-2 .ql-picker-item.ql-selected,
        [id^="ql-picker-options"] .ql-picker-item.ql-selected {
          font-weight: 500 !important;
        }

        /* Custom scrollbar */
        .ql-picker-options::-webkit-scrollbar,
        #ql-picker-options-2::-webkit-scrollbar,
        [id^="ql-picker-options"]::-webkit-scrollbar {
          width: 6px;
        }

        .ql-picker-options::-webkit-scrollbar-track,
        #ql-picker-options-2::-webkit-scrollbar-track,
        [id^="ql-picker-options"]::-webkit-scrollbar-track {
          background: transparent;
        }

        .ql-picker-options::-webkit-scrollbar-thumb,
        #ql-picker-options-2::-webkit-scrollbar-thumb,
        [id^="ql-picker-options"]::-webkit-scrollbar-thumb {
          background: hsl(var(--border));
          border-radius: 3px;
        }

        .ql-picker-options::-webkit-scrollbar-thumb:hover,
        #ql-picker-options-2::-webkit-scrollbar-thumb:hover,
        [id^="ql-picker-options"]::-webkit-scrollbar-thumb:hover {
          background: hsl(var(--primary));
        }

        /* Override Quill toolbar button hover/active states */
        .ql-snow.ql-toolbar button:hover,
        .ql-snow .ql-toolbar button:hover,
        .ql-snow.ql-toolbar button:focus,
        .ql-snow .ql-toolbar button:focus,
        .ql-snow.ql-toolbar button.ql-active,
        .ql-snow .ql-toolbar button.ql-active,
        .ql-snow.ql-toolbar .ql-picker-label:hover,
        .ql-snow .ql-toolbar .ql-picker-label:hover,
        .ql-snow.ql-toolbar .ql-picker-label.ql-active,
        .ql-snow .ql-toolbar .ql-picker-label.ql-active,
        .ql-snow.ql-toolbar .ql-picker-item:hover,
        .ql-snow .ql-toolbar .ql-picker-item:hover,
        .ql-snow.ql-toolbar .ql-picker-item.ql-selected,
        .ql-snow .ql-toolbar .ql-picker-item.ql-selected {
          color: var(--primary) !important;
        }

        /* Override Quill toolbar button SVG stroke colors */
        .ql-snow.ql-toolbar button:hover .ql-stroke,
        .ql-snow .ql-toolbar button:hover .ql-stroke,
        .ql-snow.ql-toolbar button:focus .ql-stroke,
        .ql-snow .ql-toolbar button:focus .ql-stroke,
        .ql-snow.ql-toolbar button.ql-active .ql-stroke,
        .ql-snow .ql-toolbar button.ql-active .ql-stroke,
        .ql-snow.ql-toolbar .ql-picker-label:hover .ql-stroke,
        .ql-snow .ql-toolbar .ql-picker-label:hover .ql-stroke,
        .ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-stroke,
        .ql-snow .ql-toolbar .ql-picker-label.ql-active .ql-stroke,
        .ql-snow.ql-toolbar .ql-picker-item:hover .ql-stroke,
        .ql-snow .ql-toolbar .ql-picker-item:hover .ql-stroke,
        .ql-snow.ql-toolbar .ql-picker-item.ql-selected .ql-stroke,
        .ql-snow .ql-toolbar .ql-picker-item.ql-selected .ql-stroke,
        .ql-snow.ql-toolbar button:hover .ql-stroke-miter,
        .ql-snow .ql-toolbar button:hover .ql-stroke-miter,
        .ql-snow.ql-toolbar button:focus .ql-stroke-miter,
        .ql-snow .ql-toolbar button:focus .ql-stroke-miter,
        .ql-snow.ql-toolbar button.ql-active .ql-stroke-miter,
        .ql-snow .ql-toolbar button.ql-active .ql-stroke-miter,
        .ql-snow.ql-toolbar .ql-picker-label:hover .ql-stroke-miter,
        .ql-snow .ql-toolbar .ql-picker-label:hover .ql-stroke-miter,
        .ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-stroke-miter,
        .ql-snow .ql-toolbar .ql-picker-label.ql-active .ql-stroke-miter,
        .ql-snow.ql-toolbar .ql-picker-item:hover .ql-stroke-miter,
        .ql-snow .ql-toolbar .ql-picker-item:hover .ql-stroke-miter,
        .ql-snow.ql-toolbar .ql-picker-item.ql-selected .ql-stroke-miter,
        .ql-snow .ql-toolbar .ql-picker-item.ql-selected .ql-stroke-miter {
          stroke: var(--primary) !important;
        }

        /* Override Quill toolbar button SVG fill colors */
        .ql-snow.ql-toolbar button:hover .ql-fill,
        .ql-snow .ql-toolbar button:hover .ql-fill,
        .ql-snow.ql-toolbar button:focus .ql-fill,
        .ql-snow .ql-toolbar button:focus .ql-fill,
        .ql-snow.ql-toolbar button.ql-active .ql-fill,
        .ql-snow .ql-toolbar button.ql-active .ql-fill,
        .ql-snow.ql-toolbar .ql-picker-label:hover .ql-fill,
        .ql-snow .ql-toolbar .ql-picker-label:hover .ql-fill,
        .ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-fill,
        .ql-snow .ql-toolbar .ql-picker-label.ql-active .ql-fill,
        .ql-snow.ql-toolbar .ql-picker-item:hover .ql-fill,
        .ql-snow .ql-toolbar .ql-picker-item:hover .ql-fill,
        .ql-snow.ql-toolbar .ql-picker-item.ql-selected .ql-fill,
        .ql-snow .ql-toolbar .ql-picker-item.ql-selected .ql-fill,
        .ql-snow.ql-toolbar button:hover .ql-stroke.ql-fill,
        .ql-snow .ql-toolbar button:hover .ql-stroke.ql-fill,
        .ql-snow.ql-toolbar button:focus .ql-stroke.ql-fill,
        .ql-snow .ql-toolbar button:focus .ql-stroke.ql-fill,
        .ql-snow.ql-toolbar button.ql-active .ql-stroke.ql-fill,
        .ql-snow .ql-toolbar button.ql-active .ql-stroke.ql-fill,
        .ql-snow.ql-toolbar .ql-picker-label:hover .ql-stroke.ql-fill,
        .ql-snow .ql-toolbar .ql-picker-label:hover .ql-stroke.ql-fill,
        .ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-stroke.ql-fill,
        .ql-snow .ql-toolbar .ql-picker-label.ql-active .ql-stroke.ql-fill,
        .ql-snow.ql-toolbar .ql-picker-item:hover .ql-stroke.ql-fill,
        .ql-snow .ql-toolbar .ql-picker-item:hover .ql-stroke.ql-fill,
        .ql-snow.ql-toolbar .ql-picker-item.ql-selected .ql-stroke.ql-fill,
        .ql-snow .ql-toolbar .ql-picker-item.ql-selected .ql-stroke.ql-fill {
          fill: var(--primary) !important;
        }

        /* Quill tooltip styling */
        .ql-snow .ql-tooltip {
          background: var(--secondary-background) !important;
          border: 1px solid var(--border) !important;
          border-radius: 8px !important;
          box-shadow:
            0 10px 15px -3px rgb(0 0 0 / 0.1),
            0 4px 6px -4px rgb(0 0 0 / 0.1) !important;
          color: #1e293b !important; /* Dark slate for light mode */
          padding: 5px 12px !important;
          white-space: nowrap !important;
          transform: translateX(80px) !important;
        }

        @media (prefers-color-scheme: dark) {
          .ql-snow .ql-tooltip {
            color: white !important; /* White for dark mode */
          }
        }

        /* Quill tooltip input styling */
        .ql-snow .ql-tooltip input[type="text"] {
          background: var(--background) !important;
          border: none !important;
          border-radius: 4px !important;
          padding: 4px 8px !important;
          color: #1e293b !important; /* Dark slate for light mode */
          font-size: 13px !important;
          outline: none !important;
        }

        @media (prefers-color-scheme: dark) {
          .ql-snow .ql-tooltip input[type="text"] {
            color: white !important; /* White for dark mode */
          }
        }

        /* Quill tooltip action buttons */
        .ql-snow .ql-tooltip .ql-action,
        .ql-snow .ql-tooltip .ql-remove {
          margin-left: 8px !important;
          text-decoration: none !important;
          color: var(--primary) !important;
          font-size: 12px !important;
          cursor: pointer !important;
        }

        .ql-snow .ql-tooltip .ql-action:hover,
        .ql-snow .ql-tooltip .ql-remove:hover {
          opacity: 0.8 !important;
        }

        /* Quill tooltip label */
        .ql-snow .ql-tooltip::before {
          content: "Visit URL:" !important;
          line-height: 26px !important;
          margin-right: 6px !important;
        }

        /* Header dropdown labels */
        .quill-wrapper .ql-picker.ql-header .ql-picker-label::before,
        .quill-wrapper .ql-picker.ql-header .ql-picker-item::before {
          content: "Normal";
        }

        .quill-wrapper
          .ql-picker.ql-header
          .ql-picker-label[data-value="1"]::before,
        .quill-wrapper
          .ql-picker.ql-header
          .ql-picker-item[data-value="1"]::before {
          content: "Heading 1";
          font-weight: 600;
        }

        .quill-wrapper
          .ql-picker.ql-header
          .ql-picker-label[data-value="2"]::before,
        .quill-wrapper
          .ql-picker.ql-header
          .ql-picker-item[data-value="2"]::before {
          content: "Heading 2";
          font-weight: 600;
        }

        .quill-wrapper
          .ql-picker.ql-header
          .ql-picker-label[data-value="3"]::before,
        .quill-wrapper
          .ql-picker.ql-header
          .ql-picker-item[data-value="3"]::before {
          content: "Heading 3";
          font-weight: 500;
        }

        .quill-wrapper
          .ql-picker.ql-header
          .ql-picker-label[data-value="4"]::before,
        .quill-wrapper
          .ql-picker.ql-header
          .ql-picker-item[data-value="4"]::before {
          content: "Heading 4";
          font-weight: 500;
        }

        .quill-wrapper
          .ql-picker.ql-header
          .ql-picker-label[data-value="5"]::before,
        .quill-wrapper
          .ql-picker.ql-header
          .ql-picker-item[data-value="5"]::before {
          content: "Heading 5";
          font-weight: 400;
        }
      `}</style>
    </div>
  );
}
